import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Grid, Card, CardContent, Typography, IconButton, Tooltip,
  CircularProgress, Chip, Select, MenuItem, FormControl, InputLabel, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider,
} from "@mui/material";
import {
  Refresh, FilterAlt, LinkOff, CheckCircle, Error, Warning, Replay,
  ContentCopy, Visibility, VisibilityOff, SyncAlt, SwapHoriz,
} from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const CHANNEL_LABELS = {
  whatsapp_oficial: "WhatsApp", whatsapp: "WhatsApp",
  facebook: "Facebook", instagram: "Instagram", telegram: "Telegram",
  email: "Email", voip: "VoIP", sip: "SIP",
  tiktok: "TikTok", sms: "SMS", google_business: "Google Business",
  mercadolivre: "Mercado Livre", teams: "MS Teams",
};

// Empresa operadora única (dona das conexões passthrough) — Opção A (temporária):
// ownerHubId=2 (hub "OtimizApp Homolog", o Local do próprio Central) + UUID-etiqueta dedicado.
const OPERADORA_HUB_ID = "2";
const OPERADORA_EXTERNAL_ID = "8a31d00b-394c-41c0-b376-4aa1b48ba808";

const STATUS_COLORS = {
  CONNECTED: { bg: "rgba(37,211,102,0.1)", color: "#25D366", label: "Conectado", icon: CheckCircle },
  DISCONNECTED: { bg: "rgba(107,114,128,0.15)", color: "#6B7280", label: "Desconectado", icon: LinkOff },
  ERROR: { bg: "rgba(239,68,68,0.1)", color: "#EF4444", label: "Erro", icon: Error },
  pending_auth: { bg: "rgba(245,158,11,0.1)", color: "#F59E0B", label: "Aguardando auth", icon: Warning },
  CONNECTING: { bg: "rgba(107,72,255,0.1)", color: "#6B48FF", label: "Conectando", icon: Replay },
};

export default function ConnectionsAdminPage({ showSnackbar }) {
  const [connections, setConnections] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTenant, setFilterTenant] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterTenant) filters.tenantId = filterTenant;
      if (filterChannel) filters.channel = filterChannel;
      if (filterStatus) filters.status = filterStatus;
      const [connsRes, tenantsRes] = await Promise.all([
        hubApi.adminListConnections(filters),
        tenants.length === 0 ? hubApi.adminListTenants() : Promise.resolve({ data: { tenants } }),
      ]);
      setConnections(connsRes.data?.connections || []);
      if (tenants.length === 0) setTenants(tenantsRes.data?.tenants || []);
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro ao carregar", "error");
    } finally {
      setLoading(false);
    }
  }, [filterTenant, filterChannel, filterStatus, showSnackbar, tenants.length]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterTenant, filterChannel, filterStatus]);

  const handleReconnect = async (id) => {
    try {
      await hubApi.reconnectConnection(id);
      showSnackbar?.("Reconexao solicitada");
      load();
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro", "error");
    }
  };

  /*
   * Confirmacao em Dialog do painel (nao window.confirm do navegador) e erro
   * exibindo a mensagem REAL do backend no snackbar.
   */
  const [confirmacao, setConfirmacao] = useState(null); // { tipo, conn }
  const [executando, setExecutando] = useState(false);

  const executarConfirmacao = async () => {
    if (!confirmacao) return;
    const { tipo, conn } = confirmacao;
    setExecutando(true);
    try {
      if (tipo === "disconnect") {
        await hubApi.disconnectConnection(conn.id);
        showSnackbar?.("Conexao desconectada");
      } else {
        await hubApi.deleteConnection(conn.id);
        showSnackbar?.("Conexao removida permanentemente");
      }
      setConfirmacao(null);
      load();
    } catch (err) {
      showSnackbar?.(
        err?.response?.data?.error || err?.response?.data?.message || "Erro ao executar a acao",
        "error"
      );
    } finally {
      setExecutando(false);
    }
  };

  const handleDisconnect = (conn) => setConfirmacao({ tipo: "disconnect", conn });
  const handleDelete = (conn) => setConfirmacao({ tipo: "delete", conn });

  /* ───────── Passthrough de coexistência (gestão por conexão) ───────── */
  const [coex, setCoex] = useState(null);       // conexão em gestão
  const [coexData, setCoexData] = useState(null); // credenciais + destino
  const [coexLoading, setCoexLoading] = useState(false);
  const [coexBusy, setCoexBusy] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [flipForm, setFlipForm] = useState({ destino: "hub", passthroughUrl: "", passthroughVerifyToken: "", ownerHubId: "", ownerLocalTenantExternalId: "" });

  const abrirCoex = async (conn) => {
    setCoex(conn); setCoexData(null); setShowToken(false);
    setCoexLoading(true);
    try {
      const { data } = await hubApi.connectionCredentials(conn.id);
      const d = data?.data || {};
      setCoexData(d);
      setFlipForm({
        destino: d.destino === "passthrough" ? "hub" : "passthrough", // sugere a virada oposta
        passthroughUrl: d.passthroughUrl || "",
        passthroughVerifyToken: "",
        ownerHubId: "", ownerLocalTenantExternalId: "",
      });
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro ao ler credenciais", "error");
    } finally {
      setCoexLoading(false);
    }
  };

  const copiar = async (txt, label) => {
    try { await navigator.clipboard.writeText(txt || ""); showSnackbar?.(`${label} copiado`); }
    catch { showSnackbar?.("Não foi possível copiar", "error"); }
  };

  const ressincronizar = async () => {
    setCoexBusy(true);
    try {
      const { data } = await hubApi.resyncConnectionWebhook(coex.id);
      showSnackbar?.(`Webhook ressincronizado (${data?.data?.destino})`);
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro ao ressincronizar", "error");
    } finally { setCoexBusy(false); }
  };

  const virarDestino = async () => {
    const f = flipForm;
    const payload = { destino: f.destino };
    if (f.destino === "passthrough") {
      if (!f.passthroughUrl.trim()) { showSnackbar?.("Informe a URL de destino (passthrough)", "error"); return; }
      payload.passthroughUrl = f.passthroughUrl.trim();
      if (f.passthroughVerifyToken.trim()) payload.passthroughVerifyToken = f.passthroughVerifyToken.trim();
    }
    const temHub = String(f.ownerHubId).trim() !== "";
    const temEmp = f.ownerLocalTenantExternalId.trim() !== "";
    if (temHub !== temEmp) { showSnackbar?.("ownerHubId e localTenantExternalId devem vir juntos (ou nenhum)", "error"); return; }
    if (temHub) { payload.ownerHubId = Number(f.ownerHubId); payload.ownerLocalTenantExternalId = f.ownerLocalTenantExternalId.trim(); }
    setCoexBusy(true);
    try {
      const { data } = await hubApi.flipConnectionDestino(coex.id, payload);
      showSnackbar?.(`Destino virado para ${data?.data?.destino}${data?.data?.registrado ? " (número registrado)" : ""}`);
      setCoex(null);
      load();
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro ao virar destino", "error");
    } finally { setCoexBusy(false); }
  };

  /* ───────── Gerar link de conexão passthrough (cliente novo) ───────── */
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkForm, setLinkForm] = useState({ passthroughUrl: "", passthroughVerifyToken: "", ownerHubId: OPERADORA_HUB_ID, ownerLocalTenantExternalId: OPERADORA_EXTERNAL_ID });
  const [linkResult, setLinkResult] = useState(null); // { url, expiresIn }

  const abrirGerarLink = () => {
    setLinkResult(null);
    setLinkForm({ passthroughUrl: "", passthroughVerifyToken: "", ownerHubId: OPERADORA_HUB_ID, ownerLocalTenantExternalId: OPERADORA_EXTERNAL_ID });
    setLinkOpen(true);
  };

  const gerarLink = async () => {
    const f = linkForm;
    if (!f.passthroughUrl.trim()) { showSnackbar?.("Informe a URL de destino (webhook externo)", "error"); return; }
    if (!String(f.ownerHubId).trim() || !f.ownerLocalTenantExternalId.trim()) {
      showSnackbar?.("Informe ownerHubId e localTenantExternalId da empresa operadora", "error"); return;
    }
    setLinkBusy(true);
    try {
      const payload = {
        passthroughUrl: f.passthroughUrl.trim(),
        ownerHubId: Number(f.ownerHubId),
        ownerLocalTenantExternalId: f.ownerLocalTenantExternalId.trim(),
      };
      if (f.passthroughVerifyToken.trim()) payload.passthroughVerifyToken = f.passthroughVerifyToken.trim();
      const { data } = await hubApi.createPassthroughLink(payload);
      const url = `${window.location.origin}/connect?cs=${data?.token}`;
      setLinkResult({ url, expiresIn: data?.expiresIn });
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro ao gerar o link", "error");
    } finally { setLinkBusy(false); }
  };

  const channelsInList = Array.from(new Set(connections.map((c) => c.channel)));

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>Canais (Conexoes)</Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            Todas as conexoes de canais de todos os tenants
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<SwapHoriz sx={{ fontSize: 16 }} />}
            onClick={abrirGerarLink}
            sx={{ textTransform: "none", color: "#25D366", borderColor: "rgba(37,211,102,0.4)", fontWeight: 600 }}>
            Gerar link passthrough
          </Button>
          <Tooltip title="Recarregar"><IconButton onClick={load} sx={{ color: "#6B7280" }}><Refresh /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* Filtros */}
      <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <FilterAlt sx={{ fontSize: 16, color: "#6B48FF" }} />
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#fff" }}>Filtros</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "rgba(255,255,255,0.5)" }}>Tenant</InputLabel>
                <Select
                  value={filterTenant} label="Tenant"
                  onChange={(e) => setFilterTenant(e.target.value)}
                  sx={{ color: "#fff", bgcolor: "#1a1d29", borderRadius: 2,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }}
                >
                  <MenuItem value="">Todos os tenants</MenuItem>
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>#{t.id} — {t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "rgba(255,255,255,0.5)" }}>Canal</InputLabel>
                <Select
                  value={filterChannel} label="Canal"
                  onChange={(e) => setFilterChannel(e.target.value)}
                  sx={{ color: "#fff", bgcolor: "#1a1d29", borderRadius: 2,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }}
                >
                  <MenuItem value="">Todos os canais</MenuItem>
                  {Object.entries(CHANNEL_LABELS).map(([id, label]) => (
                    <MenuItem key={id} value={id}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "rgba(255,255,255,0.5)" }}>Status</InputLabel>
                <Select
                  value={filterStatus} label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                  sx={{ color: "#fff", bgcolor: "#1a1d29", borderRadius: 2,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(STATUS_COLORS).map(([id, s]) => (
                    <MenuItem key={id} value={id}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {(filterTenant || filterChannel || filterStatus) && (
            <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                {connections.length} conexao(oes) encontrada(s)
              </Typography>
              <Button size="small" onClick={() => { setFilterTenant(""); setFilterChannel(""); setFilterStatus(""); }}
                sx={{ textTransform: "none", fontSize: 11, color: "#6B48FF" }}>
                Limpar filtros
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: "#6B48FF" }} /></Box>
      ) : connections.length === 0 ? (
        <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3, p: 6, textAlign: "center" }}>
          <LinkOff sx={{ fontSize: 48, color: "#374151", mb: 1 }} />
          <Typography sx={{ color: "#6B7280" }}>
            {filterTenant || filterChannel || filterStatus
              ? "Nenhuma conexao com os filtros atuais"
              : "Nenhuma conexao cadastrada ainda"}
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {connections.map((conn) => {
            const status = STATUS_COLORS[conn.status] || STATUS_COLORS.ERROR;
            const StatusIcon = status.icon;
            const health = conn.healthScore ?? 100;
            return (
              <Grid item xs={12} md={6} lg={4} key={conn.id}>
                <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {conn.name || `Conexao #${conn.id}`}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "#9CA3AF", mt: 0.2 }}>
                          {CHANNEL_LABELS[conn.channel] || conn.channel}
                          {conn.phoneNumber && ` • ${conn.phoneNumber}`}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        icon={<StatusIcon sx={{ fontSize: 12, color: `${status.color} !important` }} />}
                        label={status.label}
                        sx={{ fontSize: 10, height: 22, bgcolor: status.bg, color: status.color, fontWeight: 600 }}
                      />
                    </Box>

                    <Box sx={{ mt: 1, p: 1, bgcolor: "rgba(107,72,255,0.06)", borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: 10, color: "#6B7280" }}>Tenant</Typography>
                      <Typography sx={{ fontSize: 12, color: "#D1D5DB", fontWeight: 500 }}>
                        #{conn.tenantId} — {conn.tenantName}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box>
                        <Typography sx={{ fontSize: 9, color: "#6B7280" }}>Health Score</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 700,
                          color: health > 70 ? "#25D366" : health > 30 ? "#F59E0B" : "#EF4444" }}>
                          {health}/100
                        </Typography>
                      </Box>
                      {conn.consecutiveErrors > 0 && (
                        <Chip size="small" label={`${conn.consecutiveErrors} erros`}
                          sx={{ fontSize: 9, height: 18, bgcolor: "rgba(239,68,68,0.1)", color: "#EF4444" }} />
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.5, mt: 1.5, flexWrap: "wrap" }}>
                      {(conn.channel === "whatsapp_oficial" || conn.channel === "whatsapp") && (
                        <Button size="small" startIcon={<SwapHoriz sx={{ fontSize: 14 }} />}
                          onClick={() => abrirCoex(conn)}
                          sx={{ textTransform: "none", fontSize: 10, color: "#25D366" }}>
                          Coex
                        </Button>
                      )}
                      <Button size="small" onClick={() => handleReconnect(conn.id)}
                        sx={{ textTransform: "none", fontSize: 10, color: "#6B48FF" }}>
                        Reconectar
                      </Button>
                      <Button size="small" onClick={() => handleDisconnect(conn)}
                        sx={{ textTransform: "none", fontSize: 10, color: "#F59E0B" }}>
                        Desconectar
                      </Button>
                      <Button size="small" onClick={() => handleDelete(conn)}
                        sx={{ textTransform: "none", fontSize: 10, color: "#EF4444" }}>
                        Remover
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Confirmacao de acoes destrutivas — Dialog do painel, nao alert do navegador */}
      <Dialog
        open={!!confirmacao}
        onClose={() => !executando && setConfirmacao(null)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: "#111827", borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: "#fff" }}>
          {confirmacao?.tipo === "delete" ? "Remover conexao?" : "Desconectar conexao?"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#9CA3AF", fontSize: 14, lineHeight: 1.6 }}>
            {confirmacao?.tipo === "delete" ? (
              <>
                <b style={{ color: "#EF4444" }}>"{confirmacao?.conn?.name}"</b> sera removida
                permanentemente (tenant #{confirmacao?.conn?.tenantId}) e a inscricao na Meta
                sera desfeita. Esta acao nao pode ser desfeita.
              </>
            ) : (
              <>
                <b>"{confirmacao?.conn?.name}"</b> sera desconectada, mas o registro
                permanece e pode ser reconectado depois.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmacao(null)} disabled={executando} sx={{ color: "#6B7280" }}>
            Cancelar
          </Button>
          <Button
            onClick={executarConfirmacao}
            disabled={executando}
            variant="contained"
            color={confirmacao?.tipo === "delete" ? "error" : "warning"}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {executando ? <CircularProgress size={18} sx={{ color: "#fff" }} /> :
              confirmacao?.tipo === "delete" ? "Remover" : "Desconectar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gestão de coexistência (passthrough / virada) */}
      <Dialog
        open={!!coex}
        onClose={() => !coexBusy && setCoex(null)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: "#111827", borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          <SwapHoriz sx={{ color: "#25D366" }} />
          Coexistência — {coex?.name || `Conexão #${coex?.id}`}
        </DialogTitle>
        <DialogContent>
          {coexLoading || !coexData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: "#6B48FF" }} />
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Chip size="small" label={coexData.destino === "passthrough" ? "Passthrough (externo)" : "Hub"}
                  sx={{ fontSize: 11, bgcolor: coexData.destino === "passthrough" ? "rgba(245,158,11,0.15)" : "rgba(107,72,255,0.15)",
                    color: coexData.destino === "passthrough" ? "#F59E0B" : "#6B48FF", fontWeight: 700 }} />
                <Chip size="small" label={`token: ${coexData.tokenStatus || "unknown"}`}
                  sx={{ fontSize: 11, bgcolor: coexData.tokenStatus === "healthy" ? "rgba(37,211,102,0.12)" : "rgba(239,68,68,0.12)",
                    color: coexData.tokenStatus === "healthy" ? "#25D366" : "#EF4444" }} />
              </Box>

              {/* Credenciais para colar manual */}
              <Typography sx={{ fontSize: 12, color: "#9CA3AF", mb: 1 }}>
                Credenciais para colar na instalação externa:
              </Typography>
              {[
                ["WABA ID", coexData.wabaId],
                ["Phone Number ID", coexData.phoneNumberId],
                ["Business ID", coexData.businessId],
              ].map(([label, val]) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                  <Typography sx={{ fontSize: 11, color: "#6B7280", width: 120 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#D1D5DB", fontFamily: "monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {val || "—"}
                  </Typography>
                  {val && (
                    <IconButton size="small" onClick={() => copiar(val, label)} sx={{ color: "#6B48FF" }}>
                      <ContentCopy sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 11, color: "#6B7280", width: 120 }}>Access Token</Typography>
                <Typography sx={{ fontSize: 12, color: "#D1D5DB", fontFamily: "monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {showToken ? (coexData.accessToken || "—") : "••••••••••••••••••••"}
                </Typography>
                <IconButton size="small" onClick={() => setShowToken((v) => !v)} sx={{ color: "#9CA3AF" }}>
                  {showToken ? <VisibilityOff sx={{ fontSize: 14 }} /> : <Visibility sx={{ fontSize: 14 }} />}
                </IconButton>
                <IconButton size="small" onClick={() => copiar(coexData.accessToken, "Token")} sx={{ color: "#6B48FF" }}>
                  <ContentCopy sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: 10, color: "#F59E0B", mb: 2 }}>
                ⚠️ Token sensível. Se expirar, reconecte e cole o novo na instalação externa.
              </Typography>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 1.5 }} />

              {/* Webhook */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
                  Webhook na Meta ({coexData.destino === "passthrough" ? "URL externa" : "HUB"})
                </Typography>
                <Button size="small" startIcon={<SyncAlt sx={{ fontSize: 14 }} />} disabled={coexBusy}
                  onClick={ressincronizar} sx={{ textTransform: "none", fontSize: 11, color: "#6B48FF" }}>
                  Ressincronizar
                </Button>
              </Box>
              {coexData.passthroughUrl && (
                <Typography sx={{ fontSize: 10, color: "#6B7280", fontFamily: "monospace", mb: 1, wordBreak: "break-all" }}>
                  {coexData.passthroughUrl}
                </Typography>
              )}

              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 1.5 }} />

              {/* Virar destino */}
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", mb: 1 }}>Virar destino</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel sx={{ color: "rgba(255,255,255,0.5)" }}>Novo destino</InputLabel>
                <Select value={flipForm.destino} label="Novo destino"
                  onChange={(e) => setFlipForm((f) => ({ ...f, destino: e.target.value }))}
                  sx={{ color: "#fff", bgcolor: "#1a1d29", ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }}>
                  <MenuItem value="hub">Hub (o HUB recebe e envia)</MenuItem>
                  <MenuItem value="passthrough">Passthrough (instalação externa)</MenuItem>
                </Select>
              </FormControl>

              {flipForm.destino === "passthrough" ? (
                <>
                  <TextField fullWidth size="small" label="URL de destino (webhook externo)" value={flipForm.passthroughUrl}
                    onChange={(e) => setFlipForm((f) => ({ ...f, passthroughUrl: e.target.value }))}
                    placeholder="https://apioficial.exemplo.com.br/v1/webhook/8/16"
                    sx={{ mb: 1.5, input: { color: "#fff" }, label: { color: "rgba(255,255,255,0.5)" },
                      ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }} />
                  <TextField fullWidth size="small" label="verify_token (opcional)" value={flipForm.passthroughVerifyToken}
                    onChange={(e) => setFlipForm((f) => ({ ...f, passthroughVerifyToken: e.target.value }))}
                    sx={{ mb: 1, input: { color: "#fff" }, label: { color: "rgba(255,255,255,0.5)" },
                      ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }} />
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: 10, color: "#F59E0B", mb: 1 }}>
                    Ao virar para Hub o número é registrado no Cloud API (idempotente). Transfira o dono para a empresa real (os dois campos juntos):
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <TextField size="small" label="ownerHubId" value={flipForm.ownerHubId}
                      onChange={(e) => setFlipForm((f) => ({ ...f, ownerHubId: e.target.value }))}
                      sx={{ width: 140, input: { color: "#fff" }, label: { color: "rgba(255,255,255,0.5)" },
                        ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }} />
                    <TextField size="small" label="localTenantExternalId" value={flipForm.ownerLocalTenantExternalId}
                      onChange={(e) => setFlipForm((f) => ({ ...f, ownerLocalTenantExternalId: e.target.value }))}
                      sx={{ flex: 1, input: { color: "#fff" }, label: { color: "rgba(255,255,255,0.5)" },
                        ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }} />
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoex(null)} disabled={coexBusy} sx={{ color: "#6B7280" }}>Fechar</Button>
          <Button onClick={virarDestino} disabled={coexBusy || coexLoading || !coexData} variant="contained"
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#6B48FF" }}>
            {coexBusy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : `Virar para ${flipForm.destino}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gerar link de conexão passthrough (cliente novo) */}
      <Dialog
        open={linkOpen}
        onClose={() => !linkBusy && setLinkOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: "#111827", borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          <SwapHoriz sx={{ color: "#25D366" }} />
          Gerar link de conexão (passthrough)
        </DialogTitle>
        <DialogContent>
          {linkResult ? (
            <Box>
              <Typography sx={{ fontSize: 13, color: "#25D366", fontWeight: 600, mb: 1 }}>
                Link gerado — envie ao cliente:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.2, bgcolor: "#1a1d29", borderRadius: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: 12, color: "#D1D5DB", fontFamily: "monospace", flex: 1, wordBreak: "break-all" }}>
                  {linkResult.url}
                </Typography>
                <IconButton size="small" onClick={() => copiar(linkResult.url, "Link")} sx={{ color: "#6B48FF" }}>
                  <ContentCopy sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: 11, color: "#F59E0B" }}>
                ⚠️ Validade: {Math.round((linkResult.expiresIn || 600) / 60)} min. O cliente abre o link, faz o login da Meta
                e a conexão nasce em modo passthrough (webhook aponta para a instalação externa). Gere um novo se expirar.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography sx={{ fontSize: 12, color: "#9CA3AF", mb: 2 }}>
                O cliente conecta pelo nosso HUB; os eventos vão direto para a instalação externa (passthrough).
                A conexão fica sob a empresa operadora informada abaixo.
              </Typography>
              <TextField fullWidth size="small" label="URL de destino (webhook externo)" value={linkForm.passthroughUrl}
                onChange={(e) => setLinkForm((f) => ({ ...f, passthroughUrl: e.target.value }))}
                placeholder="https://apioficial.exemplo.com.br/v1/webhook/8/16"
                sx={{ mb: 1.5, input: { color: "#fff" }, label: { color: "rgba(255,255,255,0.5)" },
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }} />
              <TextField fullWidth size="small" label="verify_token (opcional)" value={linkForm.passthroughVerifyToken}
                onChange={(e) => setLinkForm((f) => ({ ...f, passthroughVerifyToken: e.target.value }))}
                sx={{ mb: 1.5, input: { color: "#fff" }, label: { color: "rgba(255,255,255,0.5)" },
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }} />
              <Typography sx={{ fontSize: 11, color: "#6B7280", mb: 0.5 }}>Empresa operadora (dona da conexão) — padrão já preenchido:</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField size="small" label="ownerHubId" value={linkForm.ownerHubId}
                  onChange={(e) => setLinkForm((f) => ({ ...f, ownerHubId: e.target.value }))}
                  sx={{ width: 140, input: { color: "#fff" }, label: { color: "rgba(255,255,255,0.5)" },
                    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }} />
                <TextField size="small" label="localTenantExternalId" value={linkForm.ownerLocalTenantExternalId}
                  onChange={(e) => setLinkForm((f) => ({ ...f, ownerLocalTenantExternalId: e.target.value }))}
                  sx={{ flex: 1, input: { color: "#fff" }, label: { color: "rgba(255,255,255,0.5)" },
                    ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" } }} />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)} disabled={linkBusy} sx={{ color: "#6B7280" }}>Fechar</Button>
          {linkResult ? (
            <Button onClick={() => setLinkResult(null)} variant="outlined"
              sx={{ textTransform: "none", fontWeight: 700, color: "#25D366", borderColor: "rgba(37,211,102,0.4)" }}>
              Gerar outro
            </Button>
          ) : (
            <Button onClick={gerarLink} disabled={linkBusy} variant="contained"
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#25D366" }}>
              {linkBusy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Gerar link"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
